import { useState, useEffect } from "react";
import { Form, Spinner } from "react-bootstrap";
import { BagFill, ArrowLeft, EnvelopeAt, LockFill, PersonFill } from "react-bootstrap-icons";
import { useNavigate, useLocation } from "react-router-dom";
import {
  auth,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
} from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import axios from "axios";
import { getHost } from "../helpers/getHost";
import ThemeToggle from "../components/reusable/ThemeToggle";

const AuthPage = () => {
  const location = useLocation();
  const [mode, setMode] = useState(
    location.pathname === "/signup" ? "signup" : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/home");
    }
  }, [user, loading, navigate]);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setError("");
  };

  const switchMode = (newMode) => {
    resetForm();
    setMode(newMode);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await logInWithEmailAndPassword(email, password);
    } catch {
      setError("Invalid email or password.");
    }
    setSubmitting(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await registerWithEmailAndPassword(name, email, password);
      if (result) {
        await axios.post(
          `${getHost()}/api/signup`,
          {
            userId: `${Date.now()}`,
            userEmail: email,
            userName: name,
          },
          {
            headers: {
              Authorization: `Bearer ${result.accessToken}`,
            },
          }
        );
      } else {
        setError("Registration failed. Email may already be in use.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const isLogin = mode === "login";
  const canSubmit = isLogin
    ? email !== "" && password !== ""
    : email !== "" && password !== "" && name !== "";

  if (loading) {
    return (
      <div className="auth-page d-flex align-items-center justify-content-center">
        <Spinner animation="border" style={{ color: "var(--accent)" }} />
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Background decoration */}
      <div className="auth-bg-accent" />

      {/* Top bar */}
      <div className="auth-topbar">
        <a
          href="/"
          className="d-flex align-items-center gap-2 text-decoration-none"
          style={{ color: "var(--text-primary)", fontSize: "14px", fontWeight: 500 }}
        >
          <ArrowLeft /> Home
        </a>
        <ThemeToggle />
      </div>

      {/* Glass card */}
      <div className="auth-card">
        {/* Brand */}
        <div className="text-center mb-4">
          <div
            className="d-inline-flex align-items-center gap-2 mb-2"
            style={{ color: "var(--accent)", fontSize: "28px", fontWeight: 800, letterSpacing: "-0.5px" }}
          >
            <BagFill /> dugam
          </div>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", margin: 0 }}>
            {isLogin
              ? "Welcome back! Sign in to continue."
              : "Create your account to get started."}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="auth-tabs">
          <button
            className={`auth-tab ${isLogin ? "active" : ""}`}
            onClick={() => switchMode("login")}
          >
            Login
          </button>
          <button
            className={`auth-tab ${!isLogin ? "active" : ""}`}
            onClick={() => switchMode("signup")}
          >
            Sign Up
          </button>
          <div
            className="auth-tab-indicator"
            style={{ transform: isLogin ? "translateX(0)" : "translateX(100%)" }}
          />
        </div>

        {/* Form */}
        <Form
          className="auth-form"
          onSubmit={isLogin ? handleLogin : handleSignup}
        >
          {/* Name field — signup only */}
          <div
            className="auth-field-slide"
            style={{
              maxHeight: isLogin ? 0 : "80px",
              opacity: isLogin ? 0 : 1,
              marginBottom: isLogin ? 0 : "16px",
              overflow: "hidden",
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div className="auth-input-group">
              <PersonFill className="auth-input-icon" />
              <Form.Control
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="auth-input"
                tabIndex={isLogin ? -1 : 0}
              />
            </div>
          </div>

          <div className="auth-input-group mb-3">
            <EnvelopeAt className="auth-input-icon" />
            <Form.Control
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
            />
          </div>

          <div className="auth-input-group mb-3">
            <LockFill className="auth-input-icon" />
            <Form.Control
              type="password"
              placeholder={isLogin ? "Password" : "Create password (6+ chars)"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              minLength={6}
              required
            />
          </div>

          {error && (
            <div
              className="mb-3 px-3 py-2"
              style={{
                fontSize: "13px",
                color: "var(--like-color)",
                background: "var(--accent-light)",
                borderRadius: "var(--radius-sm)",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={!canSubmit || submitting}
          >
            {submitting ? (
              <Spinner animation="border" size="sm" />
            ) : isLogin ? (
              "Sign In"
            ) : (
              "Create Account"
            )}
          </button>
        </Form>

        {/* About blurb */}
        <div className="auth-about">
          <p className="mb-0" style={{ fontSize: "12px", color: "var(--text-tertiary)", lineHeight: 1.5 }}>
            Dugam is a business directory that connects buyers with local
            vendors, shining a spotlight on under-represented businesses and
            building new partnerships.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
